import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Multiselect Action Bar
 *
 * Fixed bottom bar, visible whenever the app is in `isMultiselectMode`.
 * The bar runs in one of two modes depending on which selection set
 * is non-empty:
 *
 *   - Block multi-select (`state.selection`): the original behavior.
 *     Reports block count, offers Create/Remove-from-stack (when the
 *     selection lives inside a Collection or Stack), "Add to
 *     collection", and a Cancel control.
 *
 *   - Collection multi-select (`state.collectionSelection`): triggered
 *     by long-pressing a CollectionBlockView. Reports collection count,
 *     offers "Add to group", and the same Cancel control.
 *
 * Both modes share the same outer chrome and the same layout: a
 * left-aligned count chip and a right-aligned cluster of action buttons
 * pulled flush to the pill's right rail by `space-between`.
 *
 * Responsive copy (handled entirely in CSS so the DOM stays consistent
 * for screen readers):
 *   - Desktop: "5 blocks selected" + a labelled "Cancel" button.
 *   - Mobile: "5 blocks" + a 36×36 icon-only close button.
 *
 * The two modes are mutually exclusive — entering one clears the other
 * (see `enterMultiselectMode` / `enterCollectionMultiselectMode` in
 * `pouch-app.ts`).
 */
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { FaIcon } from "@shared/client/components/fa-icon";
import { faCircleNotch, faRectangleHistory, faLayerMinus, faLayerPlus, faPeoplePantsSimple, faTrashCan, faXmark, } from "@fortawesome/pro-solid-svg-icons";
import { COLLECTION_LAYER } from "@shared/types/collection";
import { showToast } from "@shared/client/components/toast/show-toast";
import { useApp } from "@/state";
import { AddToCollectionPopover } from "@/components/block-detail-view/add-to-collection-popover";
import { AddToGroupPopover } from "./add-to-group-popover";
import { blockStore } from "@/data/block-store";
import { collectionStore } from "@/data/collection-store";
import { collectionCollection } from "@/data/collection-collection";
import { createStackFromBlocks } from "@/data/create-stack-from-blocks";
import { getCollectionDetails } from "@/api/query-api";
import { STACK_DETAIL_PAGE_SIZE } from "@/components/stack-unfurl/stack-unfurl";
import { ConfiguredLogger } from "@/lib/logger";
import { track } from "@/lib/analytics";
import "./multiselect-action-bar.scss";
import { WipBorder } from "../wip-border/wip-border";
const logger = new ConfiguredLogger({ caller: "MultiselectActionBar" });
// Keep in sync with the `multiselect-action-bar-slide-down` keyframe
// duration in `multiselect-action-bar.scss`. The component stays
// mounted for this long after `isMultiselectMode` flips false so the
// CSS exit animation can play to completion before unmount.
const EXIT_DURATION_MS = 220;
export const MultiselectActionBar = memo(() => {
    const app = useApp();
    const queryClient = useQueryClient();
    // Multiselect entry is gesture-isolated: long-press fires the
    // `enterMultiselectMode` action, and drag (which now flows through
    // dnd-kit's `MouseSensor` / `TouchSensor`) never reaches the
    // long-press timer because pointer movement cancels the timer
    // before it fires. The two gestures can't overlap, so the bar
    // no longer needs a global "is reorder active" suppression.
    const isMultiselectMode = app.useStore((s) => s.ui.isMultiselectMode);
    const selection = app.useStore((s) => s.selection);
    const collectionSelection = app.useStore((s) => s.collectionSelection);
    const stackSelection = app.useStore((s) => s.stackSelection);
    const selectionContainer = app.useStore((s) => s.ui.selectionContainer);
    const { collections: allCollections } = collectionStore.useList();
    const [isPopoverOpen, setPopoverOpen] = useState(false);
    const [isGroupPopoverOpen, setGroupPopoverOpen] = useState(false);
    const [isStackPending, setStackPending] = useState(false);
    const [isUnstackPending, setUnstackPending] = useState(false);
    const [isDeleteStacksPending, setDeleteStacksPending] = useState(false);
    // Exit animation: stay mounted with a `--closing` class for
    // `EXIT_DURATION_MS` after multi-select exits so the slide-down + fade
    // can play. Re-entering during the exit window cancels the pending
    // unmount and snaps the bar back to the entrance state.
    const [isMounted, setIsMounted] = useState(isMultiselectMode);
    const [isClosing, setIsClosing] = useState(false);
    useEffect(() => {
        if (isMultiselectMode) {
            setIsMounted(true);
            setIsClosing(false);
            return;
        }
        // `isMultiselectMode` is false. Only animate out if we were just
        // mounted — the initial render with multi-select already off should
        // not flash a closing animation.
        if (isMounted === false)
            return;
        setIsClosing(true);
        // Any exit path (Cancel, Add → success, Escape, Create stack, etc.)
        // should also close the popovers so the user doesn't see them hover
        // above an exiting bar. Centralizing here covers every path that
        // calls `app.exitMultiselectMode()`.
        setPopoverOpen(false);
        setGroupPopoverOpen(false);
        const timer = setTimeout(() => {
            setIsMounted(false);
            setIsClosing(false);
        }, EXIT_DURATION_MS);
        return () => clearTimeout(timer);
    }, [isMultiselectMode, isMounted]);
    const selectedIds = useMemo(() => Array.from(selection), [selection]);
    const selectedCollectionIds = useMemo(() => Array.from(collectionSelection), [collectionSelection]);
    const selectedStackIds = useMemo(() => Array.from(stackSelection), [stackSelection]);
    const count = selectedIds.length;
    const collectionCount = selectedCollectionIds.length;
    const stackCount = selectedStackIds.length;
    // Mode priority: Stack > Collection > Block. Most specific wins —
    // a non-empty stack selection means the user long-pressed a stack
    // tile, so the bar surfaces the Unstack action and nothing else.
    const isStackMode = stackCount > 0;
    const isCollectionMode = !isStackMode && collectionCount > 0;
    // Container-aware actions: a layer-1 Collection OR a layer-0 Group
    // can lift its blocks into a fresh Stack (Stacks can sit directly
    // under either, per `LEGAL_PARENT_CHILD_LAYER_PAIRS`); a layer-2
    // Stack can ungroup its blocks back to its parent. Anywhere else,
    // neither action makes sense — the bar shows just "Add to
    // collection" + "Done".
    const canHostStack = selectionContainer?.layerIndex === COLLECTION_LAYER.collection ||
        selectionContainer?.layerIndex === COLLECTION_LAYER.group;
    const isInsideStack = selectionContainer?.layerIndex === COLLECTION_LAYER.stack;
    // The Stack's parent — needed for "Remove from stack". May be a
    // Collection (layer 1) or a Group (layer 0); both are legal parents
    // for a Stack under `LEGAL_PARENT_CHILD_LAYER_PAIRS`. We resolve it
    // from the cached collection list rather than re-fetching because
    // the user has presumably been viewing the stack and its parent is
    // in the cache.
    const parentCollectionId = useMemo(() => {
        if (!isInsideStack || !selectionContainer)
            return undefined;
        const stackRow = allCollections.find((c) => c.id === selectionContainer.collectionId);
        return stackRow?.parentCollectionId;
    }, [allCollections, isInsideStack, selectionContainer]);
    const handleDone = useCallback(() => {
        app.exitMultiselectMode();
        setPopoverOpen(false);
        setGroupPopoverOpen(false);
    }, [app]);
    const handleToggleCollectionPopover = useCallback(() => {
        if (count === 0)
            return;
        setPopoverOpen((prev) => !prev);
    }, [count]);
    const handleToggleGroupPopover = useCallback(() => {
        if (collectionCount === 0)
            return;
        setGroupPopoverOpen((prev) => !prev);
    }, [collectionCount]);
    const handleClosePopover = useCallback(() => {
        setPopoverOpen(false);
    }, []);
    const handleCloseGroupPopover = useCallback(() => {
        setGroupPopoverOpen(false);
    }, []);
    // Exit mode once the add completes successfully. Keeping the popover tied
    // to `isMultiselectMode` means the bar also disappears, so no extra state.
    const handleAdded = useCallback(() => {
        app.exitMultiselectMode();
    }, [app]);
    const handleMovedToGroup = useCallback(() => {
        app.exitMultiselectMode();
    }, [app]);
    const handleCreateStack = useCallback(async () => {
        if (count === 0 || isStackPending)
            return;
        // A Stack can be parented under a Group (layer 0) OR a Collection
        // (layer 1) — both are legal child-of-parent edges under
        // `LEGAL_PARENT_CHILD_LAYER_PAIRS`. Reject anything else (Stack
        // interior, smart collection) — the helper would hit a server-side
        // ValidationError downstream and the multiselect would unwind with
        // a confusing toast.
        if (!selectionContainer ||
            (selectionContainer.layerIndex !== COLLECTION_LAYER.collection &&
                selectionContainer.layerIndex !== COLLECTION_LAYER.group)) {
            return;
        }
        const parentId = selectionContainer.collectionId;
        const blockIds = selectedIds;
        setStackPending(true);
        try {
            track({
                name: "collection.stack_created",
                props: { source: "multiselect", block_count: blockIds.length },
            });
            // Shared create-stack flow (optimistic create + move + Haiku
            // rename) — same helper the Stream's drag-to-create-stack
            // gesture uses, so both entry points have identical
            // observability + rollback semantics. The helper handles its
            // own logging; this branch just surfaces a user-visible toast
            // and exits multiselect on success.
            const result = await createStackFromBlocks(parentId, blockIds);
            if (result.movedCount === 0) {
                showToast.warning("No blocks were moved into the stack");
            }
            // Exit multiselect immediately — create + move are done from
            // the user's POV. The rename runs in the background inside the
            // helper and flips the title in place once Haiku responds.
            app.exitMultiselectMode();
        }
        catch (e) {
            showToast.error(e instanceof Error ? e.message : "Failed to create stack");
        }
        finally {
            setStackPending(false);
        }
    }, [app, count, isStackPending, selectedIds, selectionContainer]);
    const handleRemoveFromStack = useCallback(async () => {
        if (count === 0 || isStackPending)
            return;
        if (!selectionContainer || selectionContainer.layerIndex !== COLLECTION_LAYER.stack) {
            return;
        }
        if (!parentCollectionId) {
            showToast.error("Couldn't resolve this stack's parent collection");
            return;
        }
        setStackPending(true);
        try {
            const moveResult = await collectionStore.moveBlocksBetweenParentChild(selectedIds, selectionContainer.collectionId, parentCollectionId);
            if (moveResult.movedCount === 0) {
                showToast.warning("No blocks were moved out of the stack");
            }
            app.exitMultiselectMode();
        }
        catch (e) {
            showToast.error(e instanceof Error ? e.message : "Failed to remove from stack");
        }
        finally {
            setStackPending(false);
        }
    }, [app, count, isStackPending, parentCollectionId, selectedIds, selectionContainer]);
    // Bulk delete — fire-and-forget per id with a `window.confirm` gate.
    // Mirrors the single-card `BlockCardToolbar.onDeleteClick` flow but
    // looped over the selection: optimistic local cleanup via
    // `forgetDeletedBlock` (sidebar/lasso/cards) + `exitMultiselectMode`
    // (selection set + bar dismissal), then a parallel `blockStore.remove`
    // per id. The bar closes immediately so the user sees the cards leave
    // in the same React commit; per-id server failures surface as a toast
    // without rolling back any other id.
    const handleDelete = useCallback(() => {
        if (count === 0)
            return;
        const ids = selectedIds;
        const noun = ids.length === 1 ? "block" : "blocks";
        const ok = window.confirm(`Delete ${ids.length} ${noun}? This cannot be undone from the app.`);
        if (ok === false)
            return;
        for (const id of ids)
            app.forgetDeletedBlock(id);
        app.exitMultiselectMode();
        showToast.success(`Deleted ${ids.length} ${noun}`);
        for (const id of ids) {
            blockStore.remove(id).catch(() => showToast.error("Failed to delete a block"));
        }
    }, [app, count, selectedIds]);
    // Unstack — for each selected Stack id, resolve members from the
    // shared `["stream.stackChildren", id]` TanStack Query cache (the
    // same key the inline unfurl reads, populated by app-screen's
    // prefetch on parent-collection load). `fetchQuery` returns the
    // cached page if fresh, otherwise round-trips once — both paths
    // give us the FULL member list (capped at `STACK_DETAIL_PAGE_SIZE`,
    // currently 100) rather than the 3-row preview that lives in
    // `collectionBlocksCollection`. With that resolved, every member is
    // moved back to the Stack's parent Collection.
    //
    // Missing parents (shouldn't happen for layer-2 children of the
    // active collection) are logged + skipped, not failed-loud. Empty
    // stacks are silently skipped. The Stack itself remains in the
    // parent's grid as an empty tile — server-side delete is a tracked
    // follow-up.
    const handleUnstack = useCallback(async () => {
        if (selectedStackIds.length === 0 || isUnstackPending)
            return;
        setUnstackPending(true);
        try {
            const moves = [];
            for (const stackId of selectedStackIds) {
                const stack = collectionCollection.get(stackId);
                const parentId = stack?.parentCollectionId;
                if (parentId === undefined) {
                    logger.warn("Unstack skipped: Stack's parent not in cache", { stackId });
                    continue;
                }
                const details = await queryClient.fetchQuery({
                    queryKey: ["stream.stackChildren", stackId],
                    queryFn: () => getCollectionDetails(stackId, { limit: STACK_DETAIL_PAGE_SIZE }),
                    staleTime: 30_000,
                });
                const memberIds = details.blocks.map((env) => env.block.id);
                if (memberIds.length === 0)
                    continue;
                moves.push(collectionStore.moveBlocksBetweenParentChild(memberIds, stackId, parentId));
            }
            const results = await Promise.all(moves);
            const totalMoved = results.reduce((acc, r) => acc + r.movedCount, 0);
            if (totalMoved === 0) {
                showToast.warning("No blocks to unstack");
            }
            app.exitMultiselectMode();
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            logger.error("Unstack failed", { error: msg, stackIds: selectedStackIds });
            showToast.error(`Failed to unstack: ${msg}`);
        }
        finally {
            setUnstackPending(false);
        }
    }, [app, isUnstackPending, queryClient, selectedStackIds]);
    // Bulk soft-delete of selected Stacks. Mirrors the block-delete flow:
    // `window.confirm` gate, optimistic exit + per-id parallel writes,
    // per-id failures surface as a toast without rolling back the rest.
    // Blocks INSIDE a deleted stack stay alive in the database (soft-
    // delete on a Collection doesn't cascade into membership); they
    // remain reachable from the homepage stream and from any other
    // collection they belong to. If the user wanted to keep the blocks
    // visible inside the parent grid they would use Unstack instead.
    const handleDeleteStacks = useCallback(() => {
        if (selectedStackIds.length === 0 || isDeleteStacksPending)
            return;
        const ids = selectedStackIds;
        const noun = ids.length === 1 ? "stack" : "stacks";
        const ok = window.confirm(`Delete ${ids.length} ${noun}? This cannot be undone from the app.`);
        if (ok === false)
            return;
        setDeleteStacksPending(true);
        app.exitMultiselectMode();
        showToast.success(`Deleted ${ids.length} ${noun}`);
        // Fire-and-forget. `collectionStore.softDelete` already does the
        // optimistic cache eviction (tile disappears in the same commit),
        // so we only need to surface per-id failures here.
        void Promise.allSettled(ids.map((id) => collectionStore.softDelete(id))).then((results) => {
            const failed = results.filter((r) => r.status === "rejected").length;
            if (failed > 0) {
                showToast.error(`Failed to delete ${failed} ${failed === 1 ? "stack" : "stacks"}`);
            }
            setDeleteStacksPending(false);
        });
    }, [app, isDeleteStacksPending, selectedStackIds]);
    useEffect(() => {
        if (isMultiselectMode === false)
            return;
        function onKeyDown(e) {
            if (e.key === "Escape") {
                // The popover's own Escape handler fires first (it stops propagating)
                // so the bar only reacts when the popover is closed.
                if (isPopoverOpen || isGroupPopoverOpen)
                    return;
                app.exitMultiselectMode();
            }
        }
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [app, isMultiselectMode, isPopoverOpen, isGroupPopoverOpen]);
    // Display snapshot — keeps the bar's visible state (counts + which
    // mode-specific buttons are rendered) frozen during the exit
    // animation. Without this, hitting Cancel atomically clears
    // `selection` / `selectionContainer` alongside `isMultiselectMode`,
    // and the bar visibly reflows ("5 blocks selected" → "0 blocks
    // selected", Create-stack vanishes) mid slide-down. Capturing the
    // last good values during render — only while multi-select is
    // active — means the closing render reads the prior frame's
    // numbers and the pill animates as a single rigid object.
    const liveDisplay = {
        count,
        collectionCount,
        stackCount,
        isStackMode,
        isCollectionMode,
        canHostStack,
        isInsideStack,
    };
    const lastDisplayRef = useRef(liveDisplay);
    if (isMultiselectMode) {
        lastDisplayRef.current = liveDisplay;
    }
    const display = isMultiselectMode ? liveDisplay : lastDisplayRef.current;
    if (isMounted === false)
        return null;
    // Pluralized count label, driven by the frozen snapshot during the
    // exit animation. Full string ("5 blocks selected") is always emitted
    // so screen readers + the desktop layout get the verbose form; the
    // trailing " selected" word is hidden on narrow viewports via CSS so
    // the mobile pill stays compact.
    const blockNoun = display.count === 1 ? "block" : "blocks";
    const collectionNoun = display.collectionCount === 1 ? "collection" : "collections";
    const stackNoun = display.stackCount === 1 ? "stack" : "stacks";
    const countMain = display.isStackMode
        ? `${display.stackCount} ${stackNoun}`
        : display.isCollectionMode
            ? `${display.collectionCount} ${collectionNoun}`
            : `${display.count} ${blockNoun}`;
    const cancelLabel = "Cancel multi-select";
    const barClass = ["multiselect-action-bar", isClosing && "multiselect-action-bar--closing"]
        .filter(Boolean)
        .join(" ");
    return (_jsxs("div", { className: barClass, role: "toolbar", "aria-label": "Multiselect actions", children: [_jsx(WipBorder, {}), _jsxs("span", { className: "multiselect-action-bar__count", children: [countMain, _jsx("span", { className: "multiselect-action-bar__count-suffix", children: " selected" })] }), _jsx("div", { className: "multiselect-action-bar__actions", children: display.isStackMode ? (_jsxs(_Fragment, { children: [_jsxs("button", { type: "button", className: "multiselect-action-bar__btn multiselect-action-bar__btn--primary", onClick: handleUnstack, disabled: display.stackCount === 0 || isUnstackPending || isDeleteStacksPending, "aria-label": `Unstack ${display.stackCount} selected ${stackNoun}`, children: [_jsx(FaIcon, { icon: isUnstackPending ? faCircleNotch : faLayerMinus, spin: isUnstackPending }), _jsx("span", { children: "Unstack" })] }), _jsxs("button", { type: "button", className: "multiselect-action-bar__btn multiselect-action-bar__btn--danger", onClick: handleDeleteStacks, disabled: display.stackCount === 0 || isUnstackPending || isDeleteStacksPending, "aria-label": `Delete ${display.stackCount} selected ${stackNoun}`, children: [_jsx(FaIcon, { icon: isDeleteStacksPending ? faCircleNotch : faTrashCan, spin: isDeleteStacksPending }), _jsx("span", { children: "Delete" })] }), _jsxs("button", { type: "button", className: "multiselect-action-bar__btn multiselect-action-bar__btn--ghost multiselect-action-bar__btn--close", onClick: handleDone, "aria-label": cancelLabel, children: [_jsx(FaIcon, { icon: faXmark }), _jsx("span", { className: "multiselect-action-bar__btn-label", children: "Cancel" })] })] })) : display.isCollectionMode ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "multiselect-action-bar__collection-anchor", children: [_jsxs("button", { type: "button", className: [
                                        "multiselect-action-bar__btn",
                                        "multiselect-action-bar__btn--primary",
                                        isGroupPopoverOpen && "multiselect-action-bar__btn--active",
                                    ]
                                        .filter(Boolean)
                                        .join(" "), onClick: handleToggleGroupPopover, "aria-expanded": isGroupPopoverOpen, "aria-haspopup": "dialog", "aria-label": `Add ${display.collectionCount} ${collectionNoun} to a group`, disabled: display.collectionCount === 0, children: [_jsx(FaIcon, { icon: faPeoplePantsSimple }), _jsx("span", { children: "Add to group" })] }), isGroupPopoverOpen ? (_jsx(AddToGroupPopover, { collectionIds: selectedCollectionIds, onClose: handleCloseGroupPopover, onMoved: handleMovedToGroup })) : null] }), _jsxs("button", { type: "button", className: "multiselect-action-bar__btn multiselect-action-bar__btn--ghost multiselect-action-bar__btn--close", onClick: handleDone, "aria-label": cancelLabel, children: [_jsx(FaIcon, { icon: faXmark }), _jsx("span", { className: "multiselect-action-bar__btn-label", children: "Cancel" })] })] })) : (_jsxs(_Fragment, { children: [display.canHostStack ? (_jsxs("button", { type: "button", className: "multiselect-action-bar__btn multiselect-action-bar__btn--primary", onClick: handleCreateStack, disabled: display.count === 0 || isStackPending, "aria-label": `Create stack from ${display.count} selected ${blockNoun}`, children: [_jsx(FaIcon, { icon: isStackPending ? faCircleNotch : faLayerPlus, spin: isStackPending }), _jsx("span", { children: "Create stack" })] })) : null, display.isInsideStack ? (_jsxs("button", { type: "button", className: "multiselect-action-bar__btn multiselect-action-bar__btn--primary", onClick: handleRemoveFromStack, disabled: display.count === 0 || isStackPending || !parentCollectionId, "aria-label": `Remove ${display.count} selected ${blockNoun} from stack`, children: [_jsx(FaIcon, { icon: isStackPending ? faCircleNotch : faLayerMinus, spin: isStackPending }), _jsx("span", { children: "Remove from stack" })] })) : null, _jsxs("div", { className: "multiselect-action-bar__collection-anchor", children: [_jsxs("button", { type: "button", className: [
                                        "multiselect-action-bar__btn",
                                        "multiselect-action-bar__btn--primary",
                                        isPopoverOpen && "multiselect-action-bar__btn--active",
                                    ]
                                        .filter(Boolean)
                                        .join(" "), onClick: handleToggleCollectionPopover, "aria-expanded": isPopoverOpen, "aria-haspopup": "dialog", "aria-label": `Add ${display.count} ${blockNoun} to a collection`, disabled: display.count === 0, children: [_jsx(FaIcon, { icon: faRectangleHistory }), _jsx("span", { children: "Add to collection" })] }), isPopoverOpen ? (_jsx(AddToCollectionPopover, { blockIds: selectedIds, onClose: handleClosePopover, onAdded: handleAdded })) : null] }), _jsxs("button", { type: "button", className: "multiselect-action-bar__btn multiselect-action-bar__btn--danger", onClick: handleDelete, disabled: display.count === 0, "aria-label": `Delete ${display.count} selected ${blockNoun}`, children: [_jsx(FaIcon, { icon: faTrashCan }), _jsx("span", { children: "Delete" })] }), _jsxs("button", { type: "button", className: "multiselect-action-bar__btn multiselect-action-bar__btn--ghost multiselect-action-bar__btn--close", onClick: handleDone, "aria-label": cancelLabel, children: [_jsx(FaIcon, { icon: faXmark }), _jsx("span", { className: "multiselect-action-bar__btn-label", children: "Cancel" })] })] })) })] }));
});
